import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect, useRef } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TonConnectUIProvider } from '@tonconnect/ui-react';
import { Header } from './components/Header';
import { BottomNav } from './components/BottomNav';
import { FeedbackButton } from './components/FeedbackButton';
import { OfflineBanner } from './components/OfflineBanner';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastProvider } from './components/Toast';
import { BrowsePage } from './pages/BrowsePage';
import { useDarkMode } from './hooks/useDarkMode';
import { useWalletTracking } from './hooks/useWalletTracking';
import { initAnalytics } from './lib/analytics';

const LotDetailPage = lazy(() => import('./pages/LotDetailPage').then((m) => ({ default: m.LotDetailPage })));
const CreateLotPage = lazy(() => import('./pages/CreateLotPage').then((m) => ({ default: m.CreateLotPage })));
const MyLotsPage = lazy(() => import('./pages/MyLotsPage').then((m) => ({ default: m.MyLotsPage })));
const MyOffersPage = lazy(() => import('./pages/MyOffersPage').then((m) => ({ default: m.MyOffersPage })));
const ActivityPage = lazy(() => import('./pages/ActivityPage').then((m) => ({ default: m.ActivityPage })));
const FeatureVotingPage = lazy(() => import('./pages/FeatureVotingPage').then((m) => ({ default: m.FeatureVotingPage })));
const SavedSearchesPage = lazy(() => import('./pages/SavedSearchesPage').then((m) => ({ default: m.SavedSearchesPage })));
const WantAdsPage = lazy(() => import('./pages/WantAdsPage').then((m) => ({ default: m.WantAdsPage })));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage').then((m) => ({ default: m.LeaderboardPage })));

initAnalytics();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 2,
    },
  },
});

const manifestUrl =
  import.meta.env.VITE_TON_MANIFEST_URL ||
  `${window.location.origin}/tonconnect-manifest.json`;

function WalletTracker() {
  useWalletTracking();
  return null;
}

function FocusOnRouteChange() {
  const location = useLocation();
  const isFirst = useRef(true);
  useEffect(() => {
    if (isFirst.current) { isFirst.current = false; return; }
    document.getElementById('main-content')?.focus();
  }, [location.pathname]);
  return null;
}


export function App() {
  const { isDark, toggle } = useDarkMode();

  return (
    <ErrorBoundary>
      <TonConnectUIProvider manifestUrl={manifestUrl}>
        <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <BrowserRouter>
              <WalletTracker />
              <FocusOnRouteChange />
              <div className="min-h-dvh flex flex-col bg-surface">
                <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-white focus:px-4 focus:py-2 focus:rounded focus:shadow">
                  Skip to main content
                </a>
                <OfflineBanner />
                <Header isDark={isDark} onToggleTheme={toggle} />
                <main id="main-content" className="flex-1 pb-16" tabIndex={-1}>
                  <Suspense fallback={<div className="flex justify-center py-12"><div className="animate-spin h-6 w-6 border-2 border-ton-500 border-t-transparent rounded-full" /></div>}>
                    <Routes>
                      <Route path="/" element={<BrowsePage />} />
                      <Route path="/lot/:lotId" element={<LotDetailPage />} />
                      <Route path="/create" element={<CreateLotPage />} />
                      <Route path="/me/lots" element={<MyLotsPage />} />
                      <Route path="/me/offers" element={<MyOffersPage />} />
                      <Route path="/activity" element={<ActivityPage />} />
                      <Route path="/features" element={<FeatureVotingPage />} />
                      <Route path="/me/searches" element={<SavedSearchesPage />} />
                      <Route path="/wanted" element={<WantAdsPage />} />
                      <Route path="/want-ads" element={<WantAdsPage />} />
                      <Route path="/leaderboard" element={<LeaderboardPage />} />
                    </Routes>
                  </Suspense>
                </main>
                <FeedbackButton />
                <BottomNav />
              </div>
            </BrowserRouter>
          </ToastProvider>
        </QueryClientProvider>
      </TonConnectUIProvider>
    </ErrorBoundary>
  );
}
